import PropTypes from 'prop-types';
import { noCase } from 'change-case';
import { useEffect, useRef, useState } from 'react';
// @mui
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  Typography,
  IconButton,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
  Grid,
} from '@mui/material';

// utils
import { fToNow } from '../../../utils/formatTime';
// _mock_
import { _notifications } from '../../../_mock';
// components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import { useGetAllNotifications } from '../../../rquery/queries/notificaciones';

// hooks
import useAuth from '../../../hooks/useAuth';

// ----------------------------------------------------------------------
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

export default function NotificationsPopover() {
  const bellRef = useRef();
  const { user } = useAuth();

  // obtener notificaciones activas
  const {
    data: { result: { rows, count = 0 } = {} } = {},
    isLoading,
    refetch,
    isFetching,
    status,
    fetchStatus,
  } = useGetAllNotifications(
    {
      activeNotifications: true,
      userId: user?.id,
    },
    {
      // TIEMPO DE ACTUALIZAR NOTIFICACIONES CADA 1 HORA
      refetchInterval: 216000,
      // refetchInterval: 5000,
    }
  );

  const [notifications, setNotifications] = useState(/* _notifications */ []);

  const totalUnRead = notifications?.filter((item) => item.isUnRead === true).length;

  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  useEffect(() => {
    if (rows?.length > 0) {
      const dataPrepared = rows?.map((notificacion) => ({
        id: notificacion?.id.toString(),
        title: notificacion?.nombre,
        description: notificacion?.mensaje,
        avatar: null,
        color: notificacion?.tipoNotificacion?.color,
        tipo: notificacion?.tipoNotificacion?.nombre,
        createdAt: new Date(notificacion?.createdAt),
        isUnRead: true,
      }));
      setNotifications(dataPrepared);
    }
  }, [rows]);

  const handleMarkAllAsRead = () => {
    setNotifications(
      notifications.map((notification) => ({
        ...notification,
        isUnRead: false,
      }))
    );
  };
  const handleMarkAsRead = (id) => {
    setNotifications(
      notifications.map((notification) => {
        if (notification?.id === id) {
          return { ...notification, isUnRead: false };
        }
        return notification;
      })
    );
  };

  useEffect(() => {
    const bell = bellRef.current;
    const isAllRead = notifications?.every((item) => item.isUnRead === false);

    if (isAllRead) {
      bell.classList.remove('bell');
    } else {
      bell.classList.add('bell');
    }
  }, [notifications, bellRef]);

  return (
    <Box ref={bellRef}>
      <IconButtonAnimate color={open ? 'primary' : 'default'} onClick={handleOpen} sx={{ width: 40, height: 40 }}>
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify icon="eva:bell-fill" width={20} height={20} />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 360, p: 0, mt: 1.5, ml: 0.75 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notificaciones</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Tienes {totalUnRead} mensajes no leidos
            </Typography>
          </Box>

          {totalUnRead > 0 && (
            <Tooltip title=" Mark all as read">
              <IconButton color="primary" onClick={handleMarkAllAsRead}>
                <Iconify icon="eva:done-all-fill" width={20} height={20} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
          <List
            disablePadding
            // subheader={
            //   <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
            //     Nuevas
            //   </ListSubheader>
            // }
          >
            {notifications /* .slice(0, 2) */
              .map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  handleMarkAsRead={handleMarkAsRead}
                />
              ))}
          </List>

          {/* <List
            disablePadding
            subheader={
              <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                Pasadas
              </ListSubheader>
            }
          >
            {notifications.slice(2, 5).map((notification) => (
              <NotificationItem key={notification.id} notification={notification} />
            ))}
          </List> */}
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple>
            View All
          </Button>
        </Box>
      </MenuPopover>
    </Box>
  );
}

// ----------------------------------------------------------------------

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    createdAt: PropTypes.instanceOf(Date),
    id: PropTypes.string,
    isUnRead: PropTypes.bool,
    title: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    avatar: PropTypes.any,
  }),
};

function NotificationItem({ notification, handleMarkAsRead }) {
  const { avatar, title } = renderContent(notification);
  return (
    <ListItemButton
      onClick={() => handleMarkAsRead(notification.id)}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(notification.isUnRead && {
          // bgcolor: 'action.selected',
          bgcolor: `rgba(${hexToRgb(notification?.color).r}, ${hexToRgb(notification?.color).g},${
            hexToRgb(notification?.color).b
          }, 0.05)`,
        }),
      }}
    >
      <ListItemAvatar>
        <Iconify icon={'il:bell'} color={notification?.color} width={32} height={32} />
      </ListItemAvatar>
      <ListItemText
        primary={title}
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
            {fToNow(notification.createdAt)}
          </Typography>
        }
      />
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderContent(notification) {
  const title = (
    <Box flexDirection={'row'}>
      <Typography variant="caption" sx={{ opacity: 0.5 }}>
        {notification?.tipo}
      </Typography>
      <Typography variant="subtitle2">{notification?.title}</Typography>
      <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
        {noCase(notification?.description)}
      </Typography>
    </Box>
  );

  if (notification.type === 'order_placed') {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="https://minimal-assets-api-dev.vercel.app/assets/icons/ic_notification_package.svg"
        />
      ),
      title,
    };
  }
  if (notification.type === 'order_shipped') {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="https://minimal-assets-api-dev.vercel.app/assets/icons/ic_notification_shipping.svg"
        />
      ),
      title,
    };
  }
  if (notification.type === 'mail') {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="https://minimal-assets-api-dev.vercel.app/assets/icons/ic_notification_mail.svg"
        />
      ),
      title,
    };
  }
  if (notification.type === 'chat_message') {
    return {
      avatar: (
        <img
          alt={notification.title}
          src="https://minimal-assets-api-dev.vercel.app/assets/icons/ic_notification_chat.svg"
        />
      ),
      title,
    };
  }
  return {
    avatar: notification.avatar ? <img alt={notification.title} src={notification.avatar} /> : null,
    title,
  };
}
